import React, { useEffect } from 'react';
import { Button, NativeSelect, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';

import { Request } from '../../codegen/graphql';
import { PriceChangeRequestMetadata } from '../../types/price-change-request';
import { useAppConfig } from '../../hooks/useAppConfig';

type Props = {
    request: Request;
    close: () => void;
    onConfirm: (input: {
        outcome: string;
        notes: string;
        metadata: PriceChangeRequestMetadata & {
            applyToAdvertisingZone?: boolean;
        };
    }) => void;
};

function DeclinePriceChangeRequestForm({ onConfirm, close }: Props) {
    const { procurementDeclineReasons } = useAppConfig();

    const form = useForm({
        initialValues: {
            reason: procurementDeclineReasons[0] ?? '',
            notes: '',
        },
        validate: {
            reason: value => {
                if (!value) {
                    return 'Reason is required';
                }
                return null;
            },
        },
    });

    useEffect(() => {
        if (procurementDeclineReasons.length > 0 && !form.values.reason) {
            form.setFieldValue('reason', procurementDeclineReasons[0]);
        }
    }, [procurementDeclineReasons]);

    const handleClose = () => {
        form.reset();
        close();
    };

    const handleSubmit = form.onSubmit(values => {
        onConfirm({
            outcome: 'Declined',
            notes: values.notes,
            metadata: {
                reason: values.reason,
            },
        });
        handleClose();
    });

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <NativeSelect
                data={procurementDeclineReasons}
                label="Reason For Declining"
                placeholder="Select a reason"
                required
                {...form.getInputProps('reason')}
            />
            <Textarea
                size="md"
                radius="lg"
                label="Notes"
                placeholder="Additional Notes"
                classNames={{
                    label: 'text-[16px]',
                }}
                {...form.getInputProps('notes')}
            />
            <div className="flex flex-col gap-4">
                <Button size="md" fullWidth={false} type="submit">
                    Submit
                </Button>
                <Button
                    size="md"
                    fullWidth={false}
                    variant="outline"
                    onClick={handleClose}
                >
                    Cancel
                </Button>
            </div>
        </form>
    );
}

export default DeclinePriceChangeRequestForm;
